export enum NotificationType {
  NewFollower = 'NewFollower',
  FolloweePublishedBot = 'FolloweePublishedBot',
  FolloweeUpdateBot = 'FolloweeUpdateBot',
  FolloweeAppliedCompetition = 'FolloweeAppliedCompetition',
  NewCompetitionPublished = 'NewCompetitionPublished',
  CompetitionOpensApplication = 'CompetitionOpensApplication',
  CompetitionStarts = 'CompetitionStarts',
  CompetitionEnds = 'CompetitionEnds',
  PurchasedBotUpdate = 'PurchasedBotUpdate',
  PurchasedBotDelete = 'PurchasedBotDelete',
  BotSoldMilestone = 'BotSoldMilestone',
  BotRate = 'BotRate',
}

export interface NewFollowerMeta {
  followerId: number
  followerName: string
}

export interface FolloweePublishedBotMeta {
  followeeName: string
  botId: number
}

export interface FolloweeUpdateBotMeta {
  botName: string
  botId: number
}

export interface FolloweeAppliedCompetitionMeta {
  followeeName: string
  competitionId: number
  competitionTitle: string
}

export interface NewCompetitionPublishedMeta {
  competitionId: number
  competitionTitle: string
}

export interface CompetitionOpensApplicationMeta {
  competitionId: number
  competitionTitle: string
}

export interface CompetitionStartsApplicationMeta {
  competitionId: number
  competitionTitle: string
}

export interface CompetitionEndsApplicationMeta {
  competitionId: number
  competitionTitle: string
}

export interface PurchasedBotUpdateMeta {
  botId: number
  botName: string
  botVersion: string
}

export interface PurchasedBotDeleteMeta {
  botName: string
}

export interface BotSoldMilestoneMeta {
  botId: number
  botName: string
  soldCount: number
}

export interface BotRateMeta {
  botId: number
  userName: string
  botName: string
}

export const isNotificationNewFollower = (
  type: NotificationType,
  meta: unknown,
): meta is NewFollowerMeta => type === NotificationType.NewFollower

export const isNotificationFolloweePublishedBot = (
  type: NotificationType,
  meta: unknown,
): meta is FolloweePublishedBotMeta => type === NotificationType.FolloweePublishedBot

export const isNotificationFolloweeUpdateBot = (
  type: NotificationType,
  meta: unknown,
): meta is FolloweeUpdateBotMeta => type === NotificationType.FolloweeUpdateBot

export const isNotificationFolloweeAppliedCompetition = (
  type: NotificationType,
  meta: unknown,
): meta is FolloweeAppliedCompetitionMeta => type === NotificationType.FolloweeAppliedCompetition

export const isNotificationNewCompetitionPublished = (
  type: NotificationType,
  meta: unknown,
): meta is NewCompetitionPublishedMeta => type === NotificationType.NewCompetitionPublished

export const isNotificationCompetitionOpensApplication = (
  type: NotificationType,
  meta: unknown,
): meta is CompetitionOpensApplicationMeta => type === NotificationType.CompetitionOpensApplication

export const isNotificationCompetitionStarts = (
  type: NotificationType,
  meta: unknown,
): meta is CompetitionStartsApplicationMeta => type === NotificationType.CompetitionStarts

export const isNotificationCompetitionEnds = (
  type: NotificationType,
  meta: unknown,
): meta is CompetitionEndsApplicationMeta => type === NotificationType.CompetitionEnds

export const isNotificationPurchasedBotUpdate = (
  type: NotificationType,
  meta: unknown,
): meta is PurchasedBotUpdateMeta => type === NotificationType.PurchasedBotUpdate

export const isNotificationPurchasedBotDelete = (
  type: NotificationType,
  meta: unknown,
): meta is PurchasedBotDeleteMeta => type === NotificationType.PurchasedBotDelete

export const isNotificationBotSoldMilestone = (
  type: NotificationType,
  meta: unknown,
): meta is BotSoldMilestoneMeta => type === NotificationType.BotSoldMilestone

export const isNotificationBotRate = (
  type: NotificationType,
  meta: unknown,
): meta is BotRateMeta => type === NotificationType.BotRate

export const getNotificationContent = (type: NotificationType, meta: unknown) => {
  if (isNotificationNewFollower(type, meta)) {
    return {
      text: `${meta.followerName} has followed you!`,
      href: `/account/${meta.followerId}`,
    }
  }
  if (isNotificationFolloweePublishedBot(type, meta)) {
    return {
      text: `${meta.followeeName} has released a new bot`,
      href: `/bots/${meta.botId}`,
    }
  }
  if (isNotificationFolloweeUpdateBot(type, meta)) {
    return {
      text: `${meta.botName} has a newer version!`,
      href: `/bots/${meta.botId}`,
    }
  }
  if (isNotificationFolloweeAppliedCompetition(type, meta)) {
    return {
      text: `${meta.followeeName} joined the ${meta.competitionTitle}`,
      href: `/competition/${meta.competitionId}`,
    }
  }
  if (isNotificationNewCompetitionPublished(type, meta)) {
    return {
      text: `There is a new official event: ${meta.competitionTitle}`,
      href: `/competition/${meta.competitionId}`,
    }
  }
  if (isNotificationCompetitionOpensApplication(type, meta)) {
    return {
      text: `${meta.competitionTitle} is now open to apply!`,
      href: `/competition/${meta.competitionId}`,
    }
  }
  if (isNotificationCompetitionStarts(type, meta)) {
    return {
      text: `${meta.competitionTitle} is opening now!`,
      href: `/competition/${meta.competitionId}`,
    }
  }
  if (isNotificationCompetitionEnds(type, meta)) {
    return {
      text: `${meta.competitionTitle} is end.`,
      href: `/competition/${meta.competitionId}`,
    }
  }
  if (isNotificationPurchasedBotUpdate(type, meta)) {
    return {
      text: `${meta.botName} has a new version  ${meta.botVersion}!`,
      href: `/bots/${meta.botId}`,
    }
  }
  if (isNotificationBotSoldMilestone(type, meta)) {
    return {
      text: `Your ${meta.botName} sold for ${meta.soldCount} times!`,
      href: `/bots/${meta.botId}`,
    }
  }
  if (isNotificationBotRate(type, meta)) {
    return {
      text: `${meta.userName} has rated your ${meta.botName}`,
      href: `/bots/${meta.botId}`,
    }
  }
  if (isNotificationPurchasedBotDelete(type, meta)) {
    return {
      text: `${meta.botName} has been deleted by owner`,
      href: undefined,
    }
  }
  return {
    text: 'Unknown notification',
  }
}
